import { useEffect } from 'react'
import type { JSX } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import {
  UpgradeDialog,
  Dialog,
  Button,
  List,
  CheckIcon,
  Image,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Product,
  Client,
} from '@buffer-mono/tracking-plan'

import {
  useAccount,
  useOneBufferBilling,
} from '~publish/contexts/AccountContext'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { SEGMENT_NAMES } from '~publish/legacy/constants'
import { actions as modalActions } from '~publish/legacy/modals/reducer'

import styles from './TagsUpgradeModal.module.css'

export const TagsUpgradeModal = (): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { account } = useAccount()
  const currentOrganization = account?.currentOrganization
  const billing = useOneBufferBilling()
  const canStartTrial = billing?.canStartTrial
  const openGlobalModal = useOpenGlobalModal()
  const cta = canStartTrial
    ? SEGMENT_NAMES.TAGS_LIST_LIMIT_TRIAL
    : SEGMENT_NAMES.TAGS_LIST_LIMIT_UPGRADE
  const upgradePathName = canStartTrial
    ? 'tagsLimit-trial'
    : 'tagsLimit-upgrade'

  const onCloseModal = (): void => {
    dispatch(modalActions.hideTagsLimitUpgradeModal())
  }

  useEffect(() => {
    const eventData = {
      cta,
      product: Product.Publish,
      upgradePathName,
      organizationId: currentOrganization?.id || '',
      clientName: Client.PublishWeb,
    }
    BufferTracker.upgradePathViewed(eventData)
  }, [cta, upgradePathName, currentOrganization])

  const handleUpgradeButtonClick = (): void => {
    onCloseModal()
    openGlobalModal(MODALS.planSelector, {
      cta,
      upgradePathName,
    })
  }

  return (
    <UpgradeDialog open={true} onOpenChange={onCloseModal}>
      <UpgradeDialog.Content size="xlarge" className={styles.dialogContent}>
        <UpgradeDialog.TextContent>
          <UpgradeDialog.Title>
            {t('campaigns.upgradePlanModal.title')}
          </UpgradeDialog.Title>
          <Dialog.Description>
            <Trans i18nKey="campaigns.upgradePlanModal.description" />
          </Dialog.Description>
          <List className={styles.featureList}>
            <List.Item icon={CheckIcon}>
              {t('campaigns.upgradePlanModal.bullet1')}
            </List.Item>
            <List.Item icon={CheckIcon}>
              {t('campaigns.upgradePlanModal.bullet2')}
            </List.Item>
            <List.Item icon={CheckIcon}>
              {t('campaigns.upgradePlanModal.bullet3')}
            </List.Item>
          </List>
          <UpgradeDialog.Actions>
            <Button
              variant="primary"
              size="large"
              onClick={handleUpgradeButtonClick}
            >
              {t('campaigns.upgradePlanModal.button')}
            </Button>
          </UpgradeDialog.Actions>
        </UpgradeDialog.TextContent>
        <UpgradeDialog.Image>
          <Image
            src="https://buffer-publish.s3.amazonaws.com/images/tags-upgrade_2x.png"
            alt="upgrade to unlock more tags"
            placeholder="https://buffer-publish.s3.amazonaws.com/images/tags-upgrade.png"
            className={styles.upgradeImage}
          />
        </UpgradeDialog.Image>
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}
